import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { d5m1Api, ReverseCandidate } from '../../services/d5m1Api';
import { BatchAcceptanceBridge, pwd5m1f1AcceptanceManifest } from '../../features/acceptance';
import './D5M1Form.css';

// ---- Item state with selection ----
interface ItemState {
  item: ReverseCandidate;
  selected: boolean;
}

// ---- Component ----
const D5M1Form: React.FC = () => {
  const navigate = useNavigate();
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');
  const [items, setItems] = useState<ItemState[]>([]);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [queried, setQueried] = useState(false);
  const [lastApiResponse, setLastApiResponse] = useState<unknown>(null);

  // ---- Derived state ----
  const selectedCount = items.filter((s) => s.selected).length;
  const allSelected = items.length > 0 && items.every((s) => s.selected);
  const someSelected = items.some((s) => s.selected) && !allSelected;

  // ---- Query ----
  const onQuery = useCallback(async () => {
    setLoading(true);
    setQueried(false);
    try {
      const res = await d5m1Api.queryCandidates(startNo, endNo);
      setLastApiResponse(res);
      const mapped: ItemState[] = (res.items || []).map((item) => ({
        item,
        selected: false,
      }));
      setItems(mapped);
      setQueried(true);
      if (mapped.length === 0) {
        message.info('查無符合條件的採購單');
      } else {
        message.success(`查詢到 ${mapped.length} 筆資料`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '查詢失敗');
    } finally {
      setLoading(false);
    }
  }, [startNo, endNo]);

  // Auto-load on mount + refresh when tab becomes visible again
  useEffect(() => {
    onQuery();
    const handleVisibility = () => { if (document.visibilityState === 'visible') onQuery(); };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  // ---- Toggle selection ----
  const handleToggle = useCallback((no: string, checked: boolean) => {
    setItems((prev) =>
      prev.map((s) =>
        s.item.fd110_01 === no ? { ...s, selected: checked } : s
      )
    );
  }, []);

  const handleSelectAll = useCallback((checked: boolean) => {
    setItems((prev) => prev.map((s) => ({ ...s, selected: checked })));
  }, []);

  // ---- Execute reversal ----
  const onExecute = useCallback(() => {
    if (selectedCount === 0) {
      message.warning('請先勾選要沖銷的採購單');
      return;
    }
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷已勾選的 ${selectedCount} 筆採購單？此操作不可復原。`,
      okText: '確定沖銷',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        const selectedNos = items
          .filter((s) => s.selected)
          .map((s) => s.item.fd110_01);
        setExecuting(true);
        try {
          const res = await d5m1Api.reverse(selectedNos);
          setLastApiResponse(res);
          if (res.failed === 0) {
            message.success(res.message || `沖銷完成，成功 ${res.success} 筆`);
          } else {
            message.warning(
              res.message || `成功 ${res.success} 筆，失敗 ${res.failed} 筆`
            );
            if (res.errors && res.errors.length > 0) {
              res.errors.forEach((err) => message.error(err));
            }
          }
          // 沖銷成功後重新載入清單
          await onQuery();
          setLastApiResponse(res);
        } catch (e: unknown) {
          const err = e as { response?: { data?: { error?: string } } };
          message.error(err.response?.data?.error || '沖銷執行失敗');
        } finally {
          setExecuting(false);
        }
      },
    });
  }, [selectedCount, items]);

  // ---- Table columns ----
  const columns: ColumnsType<ItemState> = [
    {
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      dataIndex: 'selected',
      key: 'selected',
      width: 50,
      align: 'center',
      render: (_: unknown, record: ItemState) => (
        <Checkbox
          checked={record.selected}
          onChange={(e) => handleToggle(record.item.fd110_01, e.target.checked)}
        />
      ),
    },
    {
      title: <span data-contract-field="fd110_01">採購單號</span>,
      dataIndex: ['item', 'fd110_01'],
      key: 'fd110_01',
      width: 110,
    },
    {
      title: <span data-contract-field="fd110_12">供應商代碼</span>,
      dataIndex: ['item', 'fd110_12'],
      key: 'fd110_12',
      width: 110,
    },
    {
      title: <span data-contract-field="fd110_11">採購日期</span>,
      dataIndex: ['item', 'fd110_11'],
      key: 'fd110_11',
      width: 110,
    },
    {
      title: <span data-contract-field="fd110_18">採購人員</span>,
      dataIndex: ['item', 'fd110_18'],
      key: 'fd110_18',
      width: 90,
    },
    {
      title: <span data-contract-field="fd110_19">幣別</span>,
      dataIndex: ['item', 'fd110_19'],
      key: 'fd110_19',
      width: 70,
    },
    {
      title: <span data-contract-field="fd110_20">匯率</span>,
      dataIndex: ['item', 'fd110_20'],
      key: 'fd110_20',
      width: 80,
      align: 'right',
    },
    {
      title: <span data-contract-field="fd110_23">採購金額</span>,
      dataIndex: ['item', 'fd110_23'],
      key: 'fd110_23',
      width: 110,
      align: 'right',
    },
    {
      title: <span data-contract-field="fd110_32">部門</span>,
      dataIndex: ['item', 'fd110_32'],
      key: 'fd110_32',
      width: 80,
    },
  ];

  // ---- Render ----
  return (
    <div className="d5m1-form">
      {(loading || executing) && (
        <div className="loading-overlay">
          {executing ? '沖銷執行中...' : '載入中...'}
        </div>
      )}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                首頁
              </a>
              <span>/</span>
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                採購管理
              </a>
              <span>/</span>
              <span className="current">採購單核准沖銷</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>採購單核准沖銷</h1>
            <span className="badge">D5M1</span>
          </div>
        </div>

        {/* Query Bar */}
        <div className="query-bar">
          <label data-contract-field="query_start_no">採購單號 .從:</label>
          <Input
            value={startNo}
            onChange={(e) => setStartNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始單號"
            style={{ width: 160 }}
            allowClear
          />
          <span className="separator" data-contract-field="query_end_no">至</span>
          <Input
            value={endNo}
            onChange={(e) => setEndNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束單號"
            style={{ width: 160 }}
            allowClear
          />
          <Button type="primary" onClick={onQuery} loading={loading} data-acceptance-action="PWD5M1-ACT-QUERY">
            確定範圍
          </Button>
        </div>

        {/* Table Card */}
        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
                {selectedCount > 0 && (
                  <span>
                    ，已勾選 <strong>{selectedCount}</strong> 筆
                  </span>
                )}
              </div>
            </div>
          )}

          {queried ? (
            <Table<ItemState>
              columns={columns}
              dataSource={items}
              rowKey={(record) => record.item.fd110_01}
              pagination={false}
              size="small"
              scroll={{ y: 420 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">
              請輸入採購單號範圍並點擊「確定範圍」查詢
            </div>
          )}
        </div>

        {/* Bottom Action Bar */}
        <div className="action-bar">
          <Button
            type="primary"
            size="large"
            disabled={selectedCount === 0 || executing}
            loading={executing}
            onClick={onExecute}
            data-acceptance-action="PWD5M1-ACT-REVERSE"
          >
            沖銷 ({selectedCount})
          </Button>
          <Button
            danger
            size="large"
            onClick={() => navigate('/erp')}
            data-acceptance-action="PWD5M1-ACT-EXIT"
          >
            離開
          </Button>
        </div>
        <BatchAcceptanceBridge
          manifest={pwd5m1f1AcceptanceManifest}
          records={items.map(state => state.item)}
          selectedKeys={items.filter(state => state.selected).map(state => state.item.fd110_01)}
          keyOf={(item) => item.fd110_01}
          queryExact={async (key) => {
            const result = await d5m1Api.queryCandidates(key, key);
            return result.items?.[0] || null;
          }}
          lastApiResponse={lastApiResponse}
          loading={loading || executing}
          onAutoFill={() => {
            const first = items[0];
            if (first) handleToggle(first.item.fd110_01, true);
          }}
          autoFillDisabled={items.length === 0}
          autoFillReason="只選取目前正式 API 回傳的第一筆可退出採購單；實際退出仍由原按鈕與正式 API 執行。"
          screenDraft={{ start_no: startNo, end_no: endNo, selected_items: items.filter(state => state.selected) }}
        />
      </div>
    </div>
  );
};

export default D5M1Form;
